import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { Button, Image, Sheet, SheetContent, SheetHeader, SheetTitle } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { UserRole } from '@repo/shared-common'
import { ClientOnly, createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import SFMapView, {
  type MapColor,
  type MapFeature,
  type MapGeoJSONData,
} from '@web/src/components/primatives/sf-map-view/sf-map-view'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import PlaceCard from '@web/src/features/place/place-card/place-card'
import {
  type PlaceListFilterFormValues,
  placeListFilterFormValuesFromResolvedParams,
} from '@web/src/features/place/place-list-filter-types'
import PlaceListSideNav from '@web/src/features/place/place-list-side-nav/place-list-side-nav'
import PlaceListTopNav from '@web/src/features/place/place-list-top-nav/place-list-top-nav'
import PlaceMapActionsMenu from '@web/src/features/place/place-map-actions-menu/place-map-actions-menu'
import { useAuth } from '@web/src/hooks/use-auth'
import { useMapBox } from '@web/src/hooks/use-mapbox'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { AttachedMediaInfo, PlaceInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { Place, PlaceCategoryEnum, PlacesQuery, PlacesQueryVariables } from 'types/graphql'
import { z } from 'zod'
import { placeMapPageHeadFromLoaderData } from './map.seo'

const DEFAULT_PLACE_MAP_DISTANCE = 500
const DEFAULT_PLACE_MAP_ZOOM = 7

type PlaceMapSearchInput = {
  page?: number
  name?: string
  distance?: number
  tags?: string | string[]
  categories?: string[]
  location?: string
  locationLat?: number
  locationLong?: number
  zoom?: number
  myPlaces?: boolean
  needsReview?: boolean
}

function normalizePlaceMapTags(tags: string | string[] | undefined): string[] | undefined {
  if (tags == null) return undefined
  return Array.isArray(tags) ? tags : tags.split(',')
}

function resolvePlaceMapLoaderDeps(search: PlaceMapSearchInput) {
  return {
    page: search.page ?? 1,
    name: search.name,
    distance: search.distance ?? DEFAULT_PLACE_MAP_DISTANCE,
    tags: normalizePlaceMapTags(search.tags),
    categories: search.categories,
    location: search.location,
    locationLat: search.locationLat,
    locationLong: search.locationLong,
    myPlaces: search.myPlaces ?? false,
    needsReview: search.needsReview ?? false,
  }
}

const PLACES_MAP_QUERY: TypedDocumentNode<PlacesQuery, PlacesQueryVariables> = gql`
  query PlacesQuery($page: Int, $filter: PlacesFilter, $sortBy: PlaceSortEnum) {
    placesPage(page: $page, filter: $filter, sortBy: $sortBy) {
      places {
        ...PlaceInfo
        firstMedia {
          ...AttachedMediaInfo
        }
      }
      count
      perPage
    }
    placeTags {
      id
      name
    }
  }
  ${PlaceInfo}
  ${AttachedMediaInfo}
`

const useFixedParams = () => {
  const search = useSearch({ strict: false })
  const { currentUser } = useAuth()

  return useMemo(() => {
    const base = resolvePlaceMapLoaderDeps(search)
    const tagsStr = base.tags?.join(',')
    return {
      page: base.page,
      name: base.name ?? '',
      distance: base.distance,
      tags: tagsStr,
      tagsArray: base.tags ?? [],
      categories: base.categories,
      location: search.location || currentUser?.profile?.location || '',
      locationLat: search.locationLat ?? currentUser?.profile?.locationLat ?? 0,
      locationLong: search.locationLong ?? currentUser?.profile?.locationLong ?? 0,
      zoom: search.zoom ?? DEFAULT_PLACE_MAP_ZOOM,
      myPlaces: base.myPlaces,
      needsReview: base.needsReview,
    }
  }, [
    search.page,
    search.name,
    search.distance,
    search.tags,
    search.categories,
    search.location,
    search.locationLat,
    search.locationLong,
    search.zoom,
    search.myPlaces,
    search.needsReview,
    currentUser?.profile?.location,
    currentUser?.profile?.locationLat,
    currentUser?.profile?.locationLong,
  ])
}

const PLACE_CATEGORY_MARKER_COLORS: Record<PlaceCategoryEnum, MapColor> = {
  CAMPING: 'green',
  NUDE_BEACH: 'brown',
  CLUB: 'pink',
  RESORT: 'blue',
  OTHER: 'red',
}

const getMarkerColor = (place: Place): MapColor => {
  return PLACE_CATEGORY_MARKER_COLORS[place.category] ?? 'red'
}

const createGeoJSONData = (places: Place[], canSeeSupportersOnlyNames: boolean): MapGeoJSONData => {
  return {
    type: 'FeatureCollection',
    features: places
      .filter((place) => place.locationLat && place.locationLong)
      .map((place) => ({
        type: 'Feature',
        properties: {
          id: place.id,
          name: place.supportersOnly && !canSeeSupportersOnlyNames ? '' : place.name,
          description: place.description,
          color: getMarkerColor(place),
          locationExact: place.locationExact,
          tags: place.tags,
          place: place,
        },
        geometry: {
          type: 'Point',
          coordinates: [place.locationLong, place.locationLat],
        },
      })),
  }
}

interface PlaceSlideUpProps {
  place: Place | null
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const PlaceSlideUp = ({ place, isOpen, onOpenChange }: PlaceSlideUpProps) => {
  const { t } = useTranslation()
  const { isAtLeastRole } = useAuth()
  const canSeeName = place != null && (!place.supportersOnly || isAtLeastRole(UserRole.PREVIEW))

  return (
    <Sheet disablePointerDismissal modal={false} onOpenChange={onOpenChange} open={isOpen && place != null}>
      <SheetContent
        className={cn(
          'gap-0 overflow-hidden rounded-t-2xl border-border bg-card p-0 pb-[env(safe-area-inset-bottom)]',
          'md:data-[side=bottom]:left-[280px]'
        )}
        showOverlay={false}
        side="bottom"
      >
        <SheetHeader className="h-10" />
        {place ? (
          <>
            <SheetTitle className="sr-only">{canSeeName ? place.name : t('Supporters Only')}</SheetTitle>
            <PlaceCard
              cardBorderRadiusClassName="rounded-none border-none"
              cardLayout="compact"
              disableHoverAnimation
              place={place}
            />
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

export const Route = createFileRoute('/(public)/(main-layout)/place/map')({
  component: PlacesMapPage,
  staticData: {
    hideFooter: true,
  },
  head: () => placeMapPageHeadFromLoaderData(),
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    distance: z.coerce.number().optional(),
    tags: z.union([z.string(), z.array(z.string())]).optional(),
    categories: z.array(z.string()).optional(),
    location: z.string().optional(),
    locationLat: z.coerce.number().optional(),
    locationLong: z.coerce.number().optional(),
    zoom: z.coerce.number().optional(),
    myPlaces: z.coerce.boolean().optional(),
    needsReview: z.coerce.boolean().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        distance: DEFAULT_PLACE_MAP_DISTANCE,
        myPlaces: false,
        needsReview: false,
        zoom: DEFAULT_PLACE_MAP_ZOOM,
        tags: [],
        categories: [],
        location: '',
        locationLat: 0,
        locationLong: 0,
        name: '',
      }),
    ],
  },
  loaderDeps: ({ search }) => resolvePlaceMapLoaderDeps(search),
  loader: async ({ context, deps }) => {
    const variables: PlacesQueryVariables = {
      page: deps.page,
      filter: {
        page: deps.page,
        name: deps.name,
        distance: deps.distance,
        tags: deps.tags,
        categories: deps.categories as PlaceCategoryEnum[] | undefined,
        location: deps.location,
        locationLat: deps.locationLat,
        locationLong: deps.locationLong,
        myPlaces: deps.myPlaces,
        needsReview: deps.needsReview,
        maxPagePlaces: 100,
      },
      sortBy: 'DISTANCE',
    }

    const { queryRef } = await queryAndPreload(context, PLACES_MAP_QUERY, { variables })
    return { queryRef }
  },
})

function PlacesMapPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useFixedParams()
  const search = useSearch({ strict: false })
  const searchRef = useRef(search)
  searchRef.current = search
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<PlacesQuery>)
  const { mapBoxToken } = useMapBox()

  /** Keep showing the last places payload while filters refetch so the map is not unmounted (avoids flicker). */
  const lastPlacesQueryRef = useRef<PlacesQuery | undefined>(undefined)
  if (data?.placesPage) {
    lastPlacesQueryRef.current = data
  }
  const displayData = data?.placesPage ? data : lastPlacesQueryRef.current

  const isInitialLoading = networkStatus === NetworkStatus.loading && !displayData?.placesPage

  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null)
  const [isSlideUpOpen, setIsSlideUpOpen] = useState(false)

  const { currentUser, hasPermission, isAuthenticated, isAtLeastRole } = useAuth()

  const handleFeatureClick = useCallback((feature: MapFeature) => {
    const place = feature.properties.place
    if (place) {
      try {
        const parsedPlace = typeof place === 'string' ? JSON.parse(place) : place
        setSelectedPlace(parsedPlace)
        setIsSlideUpOpen(true)
      } catch (parseError) {
        console.error('Error parsing place data:', parseError)
      }
    }
  }, [])

  const handleCloseSlideUp = useCallback(() => {
    setIsSlideUpOpen(false)
    setSelectedPlace(null)
  }, [])

  const handleSearchArea = useCallback(
    (center: [number, number], radiusKm: number, zoom: number) => {
      handleCloseSlideUp()
      navigate({
        search: {
          ...searchRef.current,
          locationLat: center[1],
          locationLong: center[0],
          distance: Math.round(radiusKm),
          zoom,
          location: `${center[1]}, ${center[0]}`,
          page: undefined,
          sortBy: undefined,
        },
        replace: true,
      })
    },
    [handleCloseSlideUp, navigate]
  )

  const resolvedPlaceTags = displayData?.placeTags ?? []

  const listFilterDefaults = useMemo(
    (): PlaceListFilterFormValues =>
      placeListFilterFormValuesFromResolvedParams({
        name: params.name,
        distance: params.distance,
        tags: params.tagsArray,
        categories: params.categories,
        location: params.location,
        locationLat: params.locationLat,
        locationLong: params.locationLong,
        myPlaces: params.myPlaces,
        needsReview: params.needsReview,
      }),
    [
      params.name,
      params.distance,
      params.tagsArray,
      params.categories,
      params.location,
      params.locationLat,
      params.locationLong,
      params.myPlaces,
      params.needsReview,
    ]
  )

  const listFilterResetValues = useMemo(
    (): PlaceListFilterFormValues =>
      placeListFilterFormValuesFromResolvedParams({
        name: undefined,
        distance: 10_000,
        tags: undefined,
        categories: undefined,
        location: currentUser?.profile?.location,
        locationLat: currentUser?.profile?.locationLat,
        locationLong: currentUser?.profile?.locationLong,
        sortBy: undefined,
        myPlaces: false,
        needsReview: false,
      }),
    [currentUser?.profile?.location, currentUser?.profile?.locationLat, currentUser?.profile?.locationLong]
  )

  const applyListFilters = useCallback(
    (values: PlaceListFilterFormValues) => {
      handleCloseSlideUp()
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          name: values.name || undefined,
          // Radius and starting location are not controlled by nav on the map; keep URL `distance` / `location*` (e.g. from "Search this area" / map).
          tags: values.tags?.length > 0 ? values.tags : undefined,
          categories: values.categories?.length > 0 ? values.categories : undefined,
          myPlaces: values.myPlaces || undefined,
          needsReview: values.needsReview || undefined,
          sortBy: undefined,
        },
        replace: true,
      })
    },
    [handleCloseSlideUp, navigate]
  )

  const handleReset = useCallback(() => {
    handleCloseSlideUp()
    navigate({ to: '/place/map', replace: true } as Parameters<typeof navigate>[0])
  }, [handleCloseSlideUp, navigate])

  const mapCenter = useMemo((): [number, number] => {
    if (params.locationLong && params.locationLat) {
      return [params.locationLong, params.locationLat]
    }
    if (currentUser?.profile?.locationLong && currentUser?.profile?.locationLat) {
      return [currentUser.profile.locationLong, currentUser.profile.locationLat]
    }
    return [0, 0]
  }, [params.locationLat, params.locationLong, currentUser?.profile?.locationLat, currentUser?.profile?.locationLong])

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!displayData?.placesPage) {
    return null
  }

  const geoJsonData = displayData.placesPage.places
    ? createGeoJSONData(displayData.placesPage.places as Place[], isAtLeastRole(UserRole.PREVIEW))
    : {
        type: 'FeatureCollection' as const,
        features: [],
      }

  if (!hasPermission('location:view-map')) {
    return (
      <div className="fixed inset-0 z-2">
        <Image alt={t('Map placeholder')} className="absolute inset-0 h-full w-full object-cover" src="/darkmap.webp" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute top-1/2 left-1/2 z-1 w-full max-w-xs -translate-x-1/2 -translate-y-1/2 rounded-lg bg-muted p-4 px-4 sm:max-w-sm md:max-w-md md:px-8">
          <div className="flex flex-col items-center justify-center gap-6 overflow-hidden text-center text-muted-foreground">
            <LogoFull size="md" />
            <h2 className="font-semibold text-lg md:text-xl">
              {t('The map view is only available to supporters of the website')}
            </h2>
            <p className="text-base md:text-lg">{t('Please consider supporting the site to access this feature')}</p>
            <Button className="w-full sm:w-auto" onClick={() => navigate({ to: '/supporter' })} size="lg" type="button">
              {t('Become a Supporter')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-auto min-h-[calc(100vh-56px)] w-full flex-1 overflow-hidden md:h-[calc(100vh-56px)]">
      <aside className="hidden h-full min-h-0 w-fit shrink-0 overflow-visible md:flex md:flex-col">
        <PlaceListSideNav
          defaultLocation={params.location}
          distanceFromEnabled={false}
          distanceWithinEnabled={false}
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          hasPermission={hasPermission}
          isAuthenticated={isAuthenticated}
          onFiltersApply={applyListFilters}
          onReset={handleReset}
          tags={resolvedPlaceTags}
        />
      </aside>

      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
        <div className="relative flex min-h-0 w-full flex-1">
          <div className="absolute top-0 right-0 left-0 z-2 max-h-full overflow-y-auto bg-muted px-2 py-2 md:hidden">
            <PlaceListTopNav
              defaultLocation={params.location}
              distanceFromEnabled={false}
              distanceWithinEnabled={false}
              filterDefaults={listFilterDefaults}
              filterResetValues={listFilterResetValues}
              hasPermission={hasPermission}
              isAuthenticated={isAuthenticated}
              onFiltersApply={applyListFilters}
              onReset={handleReset}
              rightBarChildren={<PlaceMapActionsMenu isAuthenticated={isAuthenticated} />}
              sortByEnabled={false}
              tags={resolvedPlaceTags}
            />
          </div>

          <div className="absolute top-2 right-2 z-2 hidden p-2 md:block">
            <PlaceMapActionsMenu isAuthenticated={isAuthenticated} />
          </div>

          <div className="absolute bottom-2 left-1/2 z-1 -translate-x-1/2 rounded-md bg-black/70 p-2 font-bold text-xs">
            {displayData.placesPage.places?.length > 100 ? (
              <div className="flex flex-col items-center justify-center gap-0 whitespace-nowrap text-center text-destructive">
                <span>{t('{{count}} Places Returned', { count: displayData.placesPage.places?.length ?? 0 })}</span>
                <span>{t('Some places will not be shown')}</span>
                <span>{t('Try a smaller search area')}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-0 text-muted-foreground">
                <span>{t('{{count}} Places Returned', { count: displayData.placesPage.places?.length ?? 0 })}</span>
              </div>
            )}
          </div>

          <ClientOnly>
            <SFMapView
              center={mapCenter}
              clusterSettings={{
                enabled: true,
                maxZoom: 10,
                radius: 30,
              }}
              geoJsonData={geoJsonData}
              height="100%"
              mapboxToken={mapBoxToken}
              onFeatureClick={handleFeatureClick}
              onSearchArea={handleSearchArea}
              showAttribution={true}
              showNavigation={true}
              showSearchButton={true}
              width="100%"
              zoom={params.zoom}
            />
          </ClientOnly>
        </div>
      </div>

      <PlaceSlideUp
        isOpen={isSlideUpOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseSlideUp()
        }}
        place={selectedPlace}
      />
    </div>
  )
}
